import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { Layout } from '../components/Layout';
import { getErrorMessage } from '../types/api-error';
import type { ReportListItem, PaginatedReportList } from '../types/admin';

const DEFAULT_LIMIT = 30;

function statusLabel(status: string) {
  if (status === 'draft') return '草稿';
  if (status === 'exported') return '已处理';
  return status;
}

function statusClass(status: string) {
  if (status === 'draft') return 'bg-yellow-50 text-yellow-700';
  if (status === 'exported') return 'bg-green-50 text-green-700';
  return 'bg-gray-50 text-gray-700';
}

export function ReportsPage() {
  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(DEFAULT_LIMIT);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const fetchReports = async (search?: string, targetPage = 1) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get<PaginatedReportList>('/reports', {
        params: {
          page: targetPage,
          limit,
          ...(search ? { q: search.trim() } : {}),
        },
      });
      const result = data && typeof data === 'object' ? data : { items: [], total: 0, page: 1, limit: DEFAULT_LIMIT };
      setReports(Array.isArray(result.items) ? result.items : []);
      setTotal(typeof result.total === 'number' ? result.total : 0);
      setPage(typeof result.page === 'number' ? result.page : 1);
    } catch (err) {
      setError(getErrorMessage(err, '加载报告失败'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchReports(q, 1);
  };

  const goToPage = (targetPage: number) => {
    if (targetPage < 1 || targetPage > totalPages) return;
    fetchReports(q, targetPage);
  };

  const startItem = total === 0 ? 0 : (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">报告管理</h2>
      </div>

      <form onSubmit={handleSearch} className="flex gap-2 mb-4">
        <input
          type="text"
          placeholder="搜索宠物名或门店名称"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 min-w-0 px-4 py-2.5 rounded-lg border border-gray-300 bg-white text-base"
        />
        <button
          type="submit"
          className="px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 active:bg-indigo-800 whitespace-nowrap"
        >
          搜索
        </button>
        <button
          type="button"
          onClick={() => {
            setQ('');
            fetchReports();
          }}
          className="px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 whitespace-nowrap"
        >
          重置
        </button>
      </form>

      {error && (
        <div className="mb-4 text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>
      )}

      {loading ? (
        <div className="text-center py-10 text-gray-500">加载中…</div>
      ) : reports.length === 0 ? (
        <div className="text-center py-10 text-gray-500">未找到报告</div>
      ) : (
        <>
          <div className="hidden sm:block bg-white rounded-xl shadow overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">门店名称</th>
                  <th className="px-4 py-3 text-left font-medium">洗护日期</th>
                  <th className="px-4 py-3 text-left font-medium">宠物名称</th>
                  <th className="px-4 py-3 text-left font-medium">品种</th>
                  <th className="px-4 py-3 text-left font-medium">美容师</th>
                  <th className="px-4 py-3 text-left font-medium">状态</th>
                  <th className="px-4 py-3 text-left font-medium">创建时间</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reports.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{r.storeNameSnapshot || '-'}</td>
                    <td className="px-4 py-3">{r.serviceDate || '-'}</td>
                    <td className="px-4 py-3">{r.petName || '-'}</td>
                    <td className="px-4 py-3">{r.breed || '-'}</td>
                    <td className="px-4 py-3">{r.groomerName || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusClass(r.status)}`}>
                        {statusLabel(r.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {r.createdAt ? new Date(r.createdAt).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sm:hidden space-y-3">
            {reports.map((r) => (
              <div key={r.id} className="bg-white rounded-xl shadow p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-gray-900">{r.storeNameSnapshot || '-'}</div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusClass(r.status)}`}>
                    {statusLabel(r.status)}
                  </span>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <div>洗护日期：{r.serviceDate || '-'}</div>
                  <div>宠物名称：{r.petName || '-'}</div>
                  <div>品种：{r.breed || '-'}</div>
                  <div>美容师：{r.groomerName || '-'}</div>
                  <div>创建时间：{r.createdAt ? new Date(r.createdAt).toLocaleString() : '-'}</div>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 px-1">
              <div className="text-sm text-gray-600">
                共 {total} 条，第 {startItem}-{endItem} 条
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => goToPage(page - 1)}
                  disabled={page <= 1}
                  className="px-3 py-1.5 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  上一页
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => goToPage(p)}
                      className={`min-w-[2rem] px-2 py-1.5 rounded-md text-sm font-medium ${
                        p === page
                          ? 'bg-indigo-600 text-white'
                          : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= totalPages}
                  className="px-3 py-1.5 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  下一页
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </Layout>
  );
}
